import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      <div className="glass-header px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="p-1 -ml-1" data-testid="privacy-back-btn">
          <ArrowLeft className="w-5 h-5 text-[#111B21]" />
        </button>
        <h1 className="text-[17px] font-semibold text-[#111B21]">Privacy Policy</h1>
      </div>
      <div className="max-w-2xl mx-auto px-5 py-6 text-[#111B21]">
        <p className="text-[13px] text-[#667781] mb-6">Last updated: February 2026</p>

        <Section title="1. Introduction">
          Welcome to CallChat AI ("we", "our", "us"). This Privacy Policy explains how we collect, use,
          disclose, and safeguard your information when you use our mobile and web application. Please read
          this policy carefully. By using CallChat, you agree to the collection and use of information in
          accordance with this policy.
        </Section>

        <Section title="2. Information We Collect">
          <BulletList items={[
            'Account Information: Name, email address, phone number, and profile picture you provide during registration.',
            'Messages & Media: Text messages, images, videos, voice messages, and files you send through CallChat. Messages are transmitted through our servers to deliver them to recipients.',
            'Usage Data: Information about how you use the app, including features accessed, time spent, and interaction patterns.',
            'Device Information: Device type, operating system, browser type, and unique device identifiers.',
            'Call Data: Metadata about voice and video calls (duration, participants, timestamps). Call content is not recorded by us.',
          ]} />
        </Section>

        <Section title="3. How We Use Your Information">
          <BulletList items={[
            'To provide, maintain, and improve CallChat services.',
            'To deliver messages, calls, and media to intended recipients.',
            'To enable contact discovery so other users can find you.',
            'To process payments and manage your credit balance.',
            'To send service notifications, security alerts, and support messages.',
            'To detect, prevent, and address technical issues and abuse.',
          ]} />
        </Section>

        <Section title="4. Data Storage & Security">
          Your data is stored on secure servers with industry-standard encryption. We implement appropriate
          technical and organizational measures to protect your personal information against unauthorized
          access, alteration, disclosure, or destruction. However, no method of transmission over the
          internet is 100% secure.
        </Section>

        <Section title="5. End-to-End Encryption">
          CallChat supports end-to-end encryption (E2EE) for messages. When E2EE is active, only you and the
          person you are communicating with can read the messages. Neither CallChat nor any third party can
          access the content of encrypted messages.
        </Section>

        <Section title="6. Third-Party Services">
          We use the following third-party services:
          <BulletList items={[
            'Agora: For real-time video and audio calls.',
            'Razorpay: For payment processing.',
            'Firebase: For push notifications and analytics.',
            'AI Services: For AI-powered features like video generation and chat summaries.',
          ]} />
          These services have their own privacy policies governing the use of your information.
        </Section>

        <Section title="7. Data Retention">
          We retain your data for as long as your account is active. When you delete your account, we delete
          your personal information, messages, and associated data. Some data may be retained in backups for
          a limited period.
        </Section>

        <Section title="8. Your Rights">
          <BulletList items={[
            'Access and download your personal data.',
            'Correct inaccurate information.',
            'Delete your account and associated data.',
            'Opt out of non-essential communications.',
          ]} />
        </Section>

        <Section title="9. Children's Privacy">
          CallChat is not intended for children under 13 years of age. We do not knowingly collect personal
          information from children under 13. If we discover that a child under 13 has provided us with
          personal information, we will delete it promptly.
        </Section>

        <Section title="10. Changes to This Policy">
          We may update this Privacy Policy from time to time. We will notify you of any changes by posting
          the new policy on this page and updating the "Last updated" date.
        </Section>

        <Section title="11. Contact Us">
          If you have questions about this Privacy Policy, contact us at:
          <p className="mt-2 text-[#00A884] font-medium">support@callchat.online</p>
        </Section>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h2 className="text-[16px] font-semibold text-[#111B21] mb-2">{title}</h2>
    <div className="text-[14px] leading-relaxed text-[#3B4A54]">{children}</div>
  </div>
);

const BulletList = ({ items }) => (
  <ul className="mt-2 space-y-1.5 pl-4">
    {items.map((item, i) => (
      <li key={i} className="text-[14px] text-[#3B4A54] leading-relaxed list-disc">{item}</li>
    ))}
  </ul>
);

export default PrivacyPolicyPage;
